import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { getApiBaseUrl } from './apiBase';

const API_URL = getApiBaseUrl();

type RefreshResponse = {
  user: {
    id: string;
    email: string;
    displayName?: string;
    avatarUrl?: string;
    isPlatformAdmin?: boolean;
  };
  tenantContext?: {
    tenantId: string;
    tenantName: string;
    tenantSlug: string;
    role: string;
  } | null;
};

// Shared constants for auth callback paths - keep in sync with router configuration in App.tsx
// Note: /github/callback is NOT included - it's a protected route for GitHub App installation
const AUTH_CALLBACK_PATHS = ['/auth/callback', '/invite'] as const;

// Protected callback pages where refresh failures shouldn't trigger logout
// (user is mid-flow and logging out would disrupt the process)
const PROTECTED_CALLBACK_PATHS = ['/github/callback'] as const;

// All public paths that don't require authentication - keep in sync with router configuration
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/register',
  // SEO pages
  '/github-change-control',
  '/soc2-change-management-evidence',
  '/outsourced-software-change-control',
  '/read-only-github-audit',
  '/ai-assisted-development-evidence',
  '/software-change-control-audit-evidence',
  '/audit-readiness',
  '/change-control-evidence',
  '/branch-protection-monitoring',
  '/ci-evidence',
  '/work-item-linking',
  '/exception-tracking',
  '/eu-ai-act-software-change-control',
  // Feature pages
  '/features/audit-timeline',
  '/features/drift-detection',
  '/features/exception-tracking',
  // Sample pages
  '/sample-timeline',
  '/sample-drift-event',
  '/sample-exception',
  // Solutions hub and marketing pages
  '/solutions',
  '/continuous-audit-evidence',
  '/engineering-audit-toil',
  '/auditor-self-serve',
  // Legal pages
  '/privacy',
  '/terms',
  '/security',
  // FAQ
  '/faq',
  // Tools (guest-accessible)
  '/tools',
  '/shared',
  // Public invite accept
  '/invite',
] as const;

// Helper to check if current page is a public or callback page (shouldn't trigger logout)
export const isPublicOrCallbackPage = (): boolean => {
  const pathname = window.location.pathname;
  return (
    PUBLIC_PATHS.some(p => pathname === p || (p !== '/' && pathname.startsWith(p + '/'))) ||
    AUTH_CALLBACK_PATHS.some(p => pathname === p) ||
    PROTECTED_CALLBACK_PATHS.some(p => pathname === p)
  );
};

// Helper to check if on an auth callback page (shouldn't attempt token refresh)
export const isAuthCallbackPage = (): boolean => {
  const pathname = window.location.pathname;
  return AUTH_CALLBACK_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));
};

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const { authToken, tokenType } = useAuthStore.getState();
  if (authToken) {
    const prefix = tokenType || 'Bearer';
    const headers = config.headers ?? {};
    if (typeof (headers as { set?: (key: string, value: string) => void }).set === 'function') {
      (headers as { set: (key: string, value: string) => void }).set('Authorization', `${prefix} ${authToken}`);
    } else {
      (headers as Record<string, string>).Authorization = `${prefix} ${authToken}`;
    }
    config.headers = headers;
  }
  return config;
});

// Track if a refresh is in progress to prevent multiple simultaneous refresh attempts
type QueuedRequest = {
  request: InternalAxiosRequestConfig & { _retry?: boolean };
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshQueue: QueuedRequest[] = [];
let refreshInFlight: Promise<RefreshResponse> | null = null;
let refreshTenantKey: string | null = null;

const addRefreshSubscriber = (
  request: InternalAxiosRequestConfig & { _retry?: boolean },
  resolve: (value: unknown) => void,
  reject: (error: unknown) => void
) => {
  refreshQueue.push({ request, resolve, reject });
};

const resolveRefreshQueue = () => {
  refreshQueue.forEach(({ request, resolve, reject }) => {
    api(request).then(resolve).catch(reject);
  });
  refreshQueue = [];
};

const rejectRefreshQueue = (error: unknown) => {
  refreshQueue.forEach(({ reject }) => reject(error));
  refreshQueue = [];
};

const refreshSession = async (tenantId?: string): Promise<RefreshResponse> => {
  const tenantKey = tenantId ?? null;

  if (refreshInFlight) {
    if (refreshTenantKey === tenantKey) {
      return refreshInFlight;
    }
    try {
      await refreshInFlight;
    } catch {
      // Ignore prior refresh failure; we'll attempt a new refresh below.
    }
  }

  refreshTenantKey = tenantKey;
  refreshInFlight = axios
    .post(`${API_URL}/api/auth/refresh`, undefined, {
      withCredentials: true,
      params: tenantId ? { tenantId } : undefined,
    })
    .then((response) => {
      const data = response.data as RefreshResponse;
      useAuthStore.getState().login(data.user as never, data.tenantContext ?? null);
      return data;
    })
    .finally(() => {
      refreshInFlight = null;
      refreshTenantKey = null;
    });

  return refreshInFlight;
};

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest?.url || 'unknown';

    // Don't try to refresh if we're on the auth callback page - new tokens are being set
    if (isAuthCallbackPage()) {
      return Promise.reject(error);
    }
    
    // Don't try to auto-refresh if the failed request was already a refresh attempt
    // This prevents circular refresh attempts when the refresh endpoint itself fails
    if (requestUrl.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    // Note: If _retry is true, this request already went through the refresh flow
    // on a previous pass through this interceptor, where isRefreshing was reset
    // (either after a successful refresh or after a failure). We therefore do not
    // attempt another refresh or modify isRefreshing here.
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isPublicOrCallbackPage()) {
        return Promise.reject(error);
      }

      // Mark as retry immediately to prevent cascading refresh attempts
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(originalRequest, resolve, reject);
        });
      }

      isRefreshing = true;

      const tenantContext = useAuthStore.getState().tenantContext;

      try {
        await refreshSession(tenantContext?.tenantId);
        isRefreshing = false;
        resolveRefreshQueue();

        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        rejectRefreshQueue(refreshError);
        
        // Only logout if not already on a public page or callback page
        if (!isPublicOrCallbackPage()) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (email: string, password: string, displayName?: string) =>
    api.post('/auth/register', { email, password, displayName }),

  login: (email: string, password: string, tenantId?: string) =>
    api.post('/auth/login', { email, password, tenantId }),

  refresh: (tenantId?: string) =>
    refreshSession(tenantId),

  /** Exchange an authorization code for a session (tokens stored in HttpOnly cookies) */
  exchange: (code: string) =>
    api.post<{
      user: { id: string; email: string; displayName?: string; avatarUrl?: string; isPlatformAdmin?: boolean };
      tenantContext: { tenantId: string; tenantName: string; tenantSlug: string; role: string } | null;
    }>('/auth/exchange', { code }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get('/auth/me'),
};

// Tenants API
export const tenantsApi = {
  list: () => api.get('/tenants'),

  get: (id: string) => api.get(`/tenants/${id}`),

  create: (name: string, slug: string) =>
    api.post('/tenants', { name, slug }),

  update: (id: string, data: { name?: string; settings?: Record<string, unknown> }) =>
    api.put(`/tenants/${id}`, data),

  getMembers: (id: string) => api.get(`/tenants/${id}/members`),

  addMember: (id: string, email: string, role: string) =>
    api.post(`/tenants/${id}/members`, { email, role }),

  updateMemberRole: (id: string, userId: string, role: string) =>
    api.put(`/tenants/${id}/members/${userId}`, { role }),

  removeMember: (id: string, userId: string) =>
    api.delete(`/tenants/${id}/members/${userId}`),
};

// Auditor Invites API
export interface AuditorInvite {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
}

export interface AuditorInvitePublic {
  email: string;
  role: string;
  expiresAt: string;
  tenantName: string;
  tenantSlug: string;
}

export interface SentAuditorInvite {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
  createdBy?: string | null;
}

export const auditorInvitesApi = {
  create: (tenantId: string, email: string, role: string = 'Auditor') =>
    api.post<{ invite: AuditorInvite; inviteUrl: string }>(`/v1/auditor-invites/tenants/${tenantId}`, { email, role }),

  get: (token: string) =>
    api.get<AuditorInvitePublic>(`/v1/auditor-invites/${encodeURIComponent(token)}`),

  accept: (token: string, payload: { password: string; passwordConfirmation: string }) =>
    api.post<{ accessToken: string; tokenType: string; tenantId: string; tenantName: string; email: string; role: string }>(`/v1/auditor-invites/${encodeURIComponent(token)}/accept`, payload),

  list: (tenantId: string) =>
    api.get<SentAuditorInvite[]>(`/v1/auditor-invites/tenants/${tenantId}`),
};

// OAuth utilities
export const startGitHubOAuth = (returnTo?: string) => {
  if (returnTo && returnTo.startsWith('/')) {
    const callbackUrl = `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`;
    window.location.href = `${API_URL}/api/auth/github/start?returnUrl=${encodeURIComponent(callbackUrl)}`;
    return;
  }

  window.location.href = `${API_URL}/api/auth/github/start`;
};

// AI Awareness API
export interface AiAwarenessStatus {
  isEnabled: boolean;
  canUseFeature: boolean;
  hasMetricsAccess: boolean;
  shareInExports: boolean;
}

export interface AiMetrics {
  totalPullRequests: number;
  aiAssistedPullRequests: number;
  aiAssistedPercentage: number;
  aiPrsWithHumanApproval: number;
  humanApprovalPercentage: number;
  aiRelatedExceptions: number;
  reportingPeriodDays: number;
  generatedAtUtc: string;
}

export interface AiAwarenessMetricsResponse {
  isMetricsAvailable: boolean;
  metrics: AiMetrics | null;
  upgradeRequired: boolean;
  message: string | null;
}

export interface AiContext {
  aiAssisted: boolean;
  signalSource: string;
  detectedAt: string;
  signalDescription: string | null;
}

export interface AiControlSuggestion {
  controlName: string;
  reason: string;
  priority: string;
  implementationHint?: string | null;
}

export interface AiControlSuggestionsResponse {
  hasSuggestions: boolean;
  aiAssistedPrCount: number;
  totalPrCount: number;
  suggestions: AiControlSuggestion[];
  message: string;
}

export interface ArtifactAiInfo {
  artifactId: string;
  hasAiSignals: boolean;
  aiContext: AiContext | null;
}

export const aiAwarenessApi = {
  /** Get AI awareness feature status for current tenant. Returns null if feature disabled (404). */
  getStatus: async (): Promise<AiAwarenessStatus | null> => {
    try {
      const response = await api.get<AiAwarenessStatus>('/ai-awareness/status');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Feature disabled
      }
      throw error;
    }
  },

  /** Get AI metrics. Returns null if feature disabled (404). */
  getMetrics: async (days: number = 30): Promise<AiAwarenessMetricsResponse | null> => {
    try {
      const response = await api.get<AiAwarenessMetricsResponse>(`/ai-awareness/metrics?days=${days}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Feature disabled
      }
      throw error;
    }
  },

  /** Get AI context for a specific artifact. Returns null if feature disabled (404). */
  getArtifactAiInfo: async (artifactId: string): Promise<ArtifactAiInfo | null> => {
    try {
      const response = await api.get<ArtifactAiInfo>(`/ai-awareness/artifacts/${artifactId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Feature disabled or artifact not found
      }
      throw error;
    }
  },

  /** Update tenant AI awareness settings */
  updateSettings: async (settings: { aiAwarenessEnabled?: boolean; aiAwarenessShareInExports?: boolean }): Promise<void> => {
    await api.put('/ai-awareness/settings', settings);
  },

  /** Get AI control suggestions for a repository */
  getSuggestions: async (repositoryId: string): Promise<AiControlSuggestionsResponse | null> => {
    try {
      const response = await api.get<AiControlSuggestionsResponse>(`/ai-awareness/repositories/${repositoryId}/suggestions`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Feature disabled or repo not found
      }
      throw error;
    }
  },
};

// Evidence Pack Sharing API
export type ShareFormat = 'pdf' | 'csv' | 'json' | 'zip';

export interface EvidencePackShare {
  shareId: string;
  shareUrl: string;
  expiresAt: string;
  watermarkMode?: string;
  allowedFormats?: string;
}

export interface PublicShareMetadata {
  id: string;
  toolType: string;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string;
  repositorySummary: string;
  shareWatermarkMode: string;
  allowedFormats: string;
}

export const evidencePackSharingApi = {
  /** Create a share link for an evidence pack (authenticated) */
  createShare: (packId: string, expiresInDays: number = 14, format?: ShareFormat | null) =>
    api.post<EvidencePackShare>(`/v1/evidence-packs/${packId}/shares`, {
      expiresInDays,
      format: format ?? undefined,
    }),

  /** Revoke a share link (authenticated) */
  revokeShare: (shareId: string) =>
    api.delete(`/v1/evidence-packs/shares/${shareId}`),

  /** Get public share metadata (anonymous) */
  getPublicShare: (token: string) =>
    axios.get<PublicShareMetadata>(`${API_URL}/api/v1/shares/${token}`, { withCredentials: false }),

  /** Download from public share (anonymous) */
  downloadPublicShare: (token: string, format: ShareFormat) =>
    `${API_URL}/api/v1/shares/${token}/download?format=${format}`,
};

export default api;
